'use client';

// Minimal hand-rolled SVG pie chart. No external dep — the bundle
// already pays for libauth + qrcode-generator + Next, no need to add
// a charting library for one widget.
//
// Slices render in input order. Pass top-N + an "other" bucket if
// you have a long tail; the component itself doesn't bucket.

const PALETTE = [
  '#7DD3FC',  // sky-300 (brand accent)
  '#34D399',  // emerald-400
  '#FBBF24',  // amber-400
  '#F87171',  // red-400
  '#A78BFA',  // violet-400
  '#FB7185',  // rose-400
  '#22D3EE',  // cyan-400
  '#FACC15',  // yellow-400
  '#94A3B8',  // slate-400 — "other" / fallback
];

interface PieSlice {
  label: string;
  value: number;        // any positive number; will be normalized
}

export function PieChart({ slices, size = 180 }: { slices: PieSlice[]; size?: number }) {
  const positive = slices.filter((s) => s.value > 0);
  const total    = positive.reduce((sum, s) => sum + s.value, 0);

  if (total === 0) {
    return (
      <div className="text-xs text-ink-faint flex items-center justify-center"
           style={{ width: size, height: size }}>
        no data
      </div>
    );
  }

  // SVG geometry: viewBox is centered around 0; each arc is computed
  // from cumulative angle. Single-slice case (one method covering
  // 100%) needs special handling because an SVG arc from p→p with
  // sweep flag still draws as a degenerate point — emit a full
  // circle instead.
  const r = size / 2;
  const paths: { d: string; color: string; label: string; pct: number }[] = [];
  let acc = 0;
  positive.forEach((slice, i) => {
    const frac = slice.value / total;
    const a0 = 2 * Math.PI * acc;
    const a1 = 2 * Math.PI * (acc + frac);
    acc += frac;
    const color = PALETTE[i % PALETTE.length]!;

    let d: string;
    if (frac >= 1 - 1e-9) {
      // Single-slice: draw two half-circles to make a full disk.
      d = `M ${r} 0 A ${r} ${r} 0 1 1 ${r} ${2 * r} A ${r} ${r} 0 1 1 ${r} 0 Z`;
    } else {
      const x0 = r + r * Math.sin(a0);
      const y0 = r - r * Math.cos(a0);
      const x1 = r + r * Math.sin(a1);
      const y1 = r - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      d = `M ${r} ${r} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    }
    paths.push({ d, color, label: slice.label, pct: frac * 100 });
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label="usage distribution by method">
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill={p.color}>
            <title>{p.label}: {p.pct.toFixed(1)}%</title>
          </path>
        ))}
      </svg>
      <ul className="text-xs space-y-1.5 min-w-0">
        {paths.map((p, i) => (
          <li key={i} className="flex items-baseline gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm flex-none"
                  style={{ background: p.color }} />
            <span className="font-mono text-ink truncate" title={p.label}>{p.label}</span>
            <span className="text-ink-faint ml-auto whitespace-nowrap">{p.pct.toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
